import { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import useForm from '../../lib/hooks/useForm';

import { EMAIL, SMS, REVIEW, AUTOSAVE_TIMER } from './utils/constants';
import { STEPS } from './utils/steps';
import { broadcastSchema } from './utils/broadcastSchema';
import {
  useTestBroadcastMessageMutation,
  useUpdateBroadcastMessageMutation,
  useSendBroadcastMessageMutation,
  useCreateBroadcastMessageMutation,
} from './hooks/useBroadcastMessagesMutation';
import {
  broadcastMessageKey,
  useFetchBroadcastMessage,
} from './hooks/useFetchBroadcastMessages';
import { navigateBack } from './utils/navigation';

export const useBroadcastForm = ({ currentStep }) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const { testBroadcastMessageMutation } = useTestBroadcastMessageMutation();
  const {
    updateBroadcastMessageMutation,
  } = useUpdateBroadcastMessageMutation();
  const {
    isSending,
    sendBroadcastMessageMutation,
  } = useSendBroadcastMessageMutation();
  const {
    createBroadcastMessageMutation,
  } = useCreateBroadcastMessageMutation();
  const { broadcastMessage, isFetching } = useFetchBroadcastMessage({
    id: params.broadcastId,
  });
  const [autoSaved, setAutoSaved] = useState({
    time: '',
    status: '',
  });

  const {
    status,
    broadcastType,
    subject,
    emailBody,
    smsBody,
    uuid,
    campaignUuid,
    audiences,
    trackLinks,
    recipientFields,
  } = broadcastMessage;

  const initialValues = {
    status: status || 'draft',
    currentStep,
    broadcastType: broadcastType || 'both', // sms, email, both
    subject: subject || '',
    emailBody: emailBody || '',
    smsBody: smsBody || '',
    uuid: uuid || '',
    campaignUuid: campaignUuid || null,
    audiences: audiences || [],
    trackLinks: trackLinks ?? true,
  };

  const form = useForm({
    initialValues,
    validationSchema: broadcastSchema,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const stepOrder = () => {
    switch (form.values.broadcastType) {
      case EMAIL:
        return Object.keys(STEPS).filter(stepId => stepId !== SMS);
      case SMS:
        return Object.keys(STEPS).filter(stepId => stepId !== EMAIL);
      default:
        return Object.keys(STEPS);
    }
  };

  const beforeLastStep = () => {
    const reviewStepIndex = stepOrder().indexOf(REVIEW);

    return stepOrder()[reviewStepIndex - 1];
  };

  const goToStep = increment => {
    const currentStepIndex = stepOrder().indexOf(currentStep);
    const nextStepIndex = currentStepIndex + increment;

    const nextStep = stepOrder()[nextStepIndex];

    if (!nextStep) return;

    if (form.values.uuid) {
      updateBroadcastMessageMutation(
        { ...form.values, currentStep: nextStep },
        {
          onSuccess: ({ data }) => {
            history.push(
              `/edit/${data.broadcastMessage.currentStep}/${data.broadcastMessage.uuid}${location.search}`,
            );
            form.setFieldValue('uuid', data.broadcastMessage.uuid);
            form.setFieldValue('currentStep', nextStep);
          },
        },
      );
    } else {
      createBroadcastMessageMutation(
        { ...form.values, currentStep: nextStep },
        {
          onSuccess: ({ data }) => {
            history.push(
              `/edit/${data.broadcastMessage.currentStep}/${data.broadcastMessage.uuid}${location.search}`,
            );
            form.setFieldValue('uuid', data.broadcastMessage.uuid);
            form.setFieldValue('currentStep', nextStep);
          },
        },
      );
    }
  };

  useEffect(() => {
    if (!form.dirty || !form.values.uuid) return undefined;

    const timer = setTimeout(() => {
      setAutoSaved({ time: '', status: 'saving' });
      updateBroadcastMessageMutation(
        { ...form.values },
        {
          onSuccess: () => {
            setAutoSaved({
              time: format(new Date(), 'h:mm aaa'),
              status: 'saved',
            });
          },
        },
      );
    }, AUTOSAVE_TIMER);
    return () => clearTimeout(timer);
  }, [form, form.dirty, form.values.uuid, updateBroadcastMessageMutation]);

  // custom handleChange fn needed b/c tinyMce editor input is an iFrame
  const handleEmailBodyChange = value => {
    form.setFieldValue('emailBody', value);
  };

  // asUserUuid renders the test as that recipient (SendTest still delivers to
  // the current admin); previewName is only used for the success toast.
  const sendTest = (asUserUuid, previewName) => {
    testBroadcastMessageMutation({
      id: form.values.uuid,
      asUserUuid,
      previewName,
    });
  };

  const sendBroadcast = () => {
    sendBroadcastMessageMutation(
      { ...form.values },
      {
        onSuccess: () => {
          navigateBack(history, location);
        },
        onError: () => {
          queryClient.setQueryData(
            broadcastMessageKey(broadcastMessage.uuid),
            broadcastMessage,
          );
          history.push(`/${broadcastMessage.uuid}${location.search}`);
        },
      },
    );
  };

  const scheduleBroadcast = ({ scheduledDate, scheduledHour }) => {
    sendBroadcastMessageMutation(
      { ...form.values, scheduledDate, scheduledHour },
      {
        onSuccess: () => {
          navigateBack(history, location);
        },
        onError: () => {
          queryClient.setQueryData(
            broadcastMessageKey(broadcastMessage.uuid),
            broadcastMessage,
          );
          history.push(`/${broadcastMessage.uuid}${location.search}`);
        },
      },
    );
  };

  const saveAndCloseDraft = () => {
    if (form.values.uuid) {
      updateBroadcastMessageMutation(
        { ...form.values },
        {
          onSuccess: () => {
            navigateBack(history, location);
          },
        },
      );
    } else {
      navigateBack(history, location);
    }
  };

  const saveAsDraft = draftDate => {
    const payload = { ...form.values };
    if (draftDate) {
      payload.draftDate = format(draftDate, 'yyyy-MM-dd');
    }
    if (form.values.uuid) {
      updateBroadcastMessageMutation(payload, {
        onSuccess: () => navigateBack(history, location),
      });
    } else {
      createBroadcastMessageMutation(
        { ...payload, currentStep },
        {
          onSuccess: () => navigateBack(history, location),
        },
      );
    }
  };

  const createBroadcastOnAudience = selectedAudiences => {
    const audienceIds = (selectedAudiences || []).map(a => a.value);
    createBroadcastMessageMutation(
      { ...form.values, audienceIds, currentStep },
      {
        onSuccess: ({ data }) => {
          form.setFieldValue('audiences', selectedAudiences || []);
          history.push(
            `/edit/audience/${data.broadcastMessage.uuid}${location.search}`,
            {
              shallow: true,
            },
          );
        },
      },
    );
  };

  const updateAudiences = selectedAudiences => {
    const audienceIds = (selectedAudiences || []).map(a => a.value);
    updateBroadcastMessageMutation(
      { ...form.values, audienceIds },
      {
        onSuccess: () => {
          form.setFieldValue('audiences', selectedAudiences || []);
          queryClient.invalidateQueries(['broadcastRecipients']);
        },
      },
    );
  };

  return {
    ...form,
    goToStep,
    sendBroadcast,
    scheduleBroadcast,
    saveAndCloseDraft,
    saveAsDraft,
    updateAudiences,
    createBroadcastOnAudience,
    sendTest,
    beforeLastStep,
    handleEmailBodyChange,
    isSending,
    isFetching,
    autoSaved,
    // Kept out of the Formik values on purpose: the form reinitializes on
    // initialValues changes, and recipient fields change via their own
    // endpoints.
    recipientFields: recipientFields || [],
  };
};
